import type { NavigationItem } from '@app/src/context/ScrollNavigationContext'
import { ScrollNavigationMenuItem } from '@app/src/context/ScrollNavigationMenuItem'
import type { Breakpoint, SxProps, Theme } from '@mui/material'
import { List, ListItemButton, ListItemText, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useMemo, useRef } from 'react'

const commonListItemButtonStyles = {
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}

const commonListItemTextTypographyProps = (isActive: boolean) => ({
  variant: 'body1' as const,
  fontWeight: isActive ? 600 : 400,
  color: isActive ? 'primary.main' : 'text.secondary',
})

interface ResponsiveScrollNavigationProps {
  items: NavigationItem[]
  sx?: SxProps<Theme>
  breakpoint?: Breakpoint
}

const ResponsiveScrollNavigation: React.FC<ResponsiveScrollNavigationProps> = ({ items, sx, breakpoint = 'lg' }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(breakpoint))

  return isDesktop ? <ScrollNavigationList items={items} sx={sx} /> : <ScrollNavigationTabs items={items} sx={sx} />
}

export default ResponsiveScrollNavigation

/**
 * Props shared between ScrollNavigationList and ScrollNavigationTabs components
 */
interface ScrollNavigationListProps {
  items: NavigationItem[]
  sx?: SxProps<Theme>
}

/**
 * Vertical navigation list component for wider viewports.
 * Displays navigation items in a column with right-aligned text and
 * highlights the active item with a right border.
 *
 * @component
 */
const ScrollNavigationList: React.FC<ScrollNavigationListProps> = ({ items, sx }) => {
  const listStyles: SxProps<Theme> = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      overflow: 'visible',
      width: '100%',
      ...(sx || {}),
    }),
    [sx],
  )

  return (
    <List disablePadding sx={listStyles}>
      {items.map(item => (
        <ScrollNavigationMenuItem key={item.id} id={item.id}>
          {(isActive, onClick) => (
            <ListItemButton
              selected={isActive}
              onClick={onClick}
              sx={{
                minWidth: 'unset',
                flex: 1,
                borderLeft: isActive ? '3px solid' : '3px solid transparent',
                ...commonListItemButtonStyles,
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  ...commonListItemTextTypographyProps(isActive),
                  textAlign: 'left',
                  whiteSpace: 'normal',
                }}
              />
            </ListItemButton>
          )}
        </ScrollNavigationMenuItem>
      ))}
    </List>
  )
}

/**
 * Horizontal navigation tabs component for narrow viewports.
 * Displays navigation items in a row with center-aligned text and
 * highlights the active item with a bottom border. Includes smooth
 * scrolling behavior to center the active tab.
 *
 * @component
 */
const ScrollNavigationTabs: React.FC<ScrollNavigationListProps> = ({ items, sx }) => {
  const listRef = useRef<HTMLUListElement>(null)

  const scrollActiveTabToCenter = useCallback((element: HTMLElement) => {
    const parentList = element.parentElement
    if (parentList) {
      const elementRect = element.getBoundingClientRect()
      const parentRect = parentList.getBoundingClientRect()
      const scrollLeft = elementRect.left - parentRect.left - (parentRect.width - elementRect.width) / 2
      parentList.scrollTo({
        left: parentList.scrollLeft + scrollLeft,
        behavior: 'smooth',
      })
    }
  }, [])

  const listStyles: SxProps<Theme> = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'row',
      overflow: 'auto',
      width: '100%',
      ...(sx || {}),
    }),
    [sx],
  )

  return (
    <List ref={listRef} disablePadding sx={listStyles}>
      {items.map(item => (
        <ScrollNavigationMenuItem key={item.id} id={item.id}>
          {(isActive, onClick) => (
            <ListItemButton
              selected={isActive}
              onClick={e => {
                onClick()
                scrollActiveTabToCenter(e.currentTarget)
              }}
              ref={node => {
                if (node && isActive) {
                  scrollActiveTabToCenter(node)
                }
              }}
              sx={{
                minWidth: 'auto',
                flex: '0 0 auto',
                borderBottom: isActive ? '3px solid' : '3px solid transparent',
                ...commonListItemButtonStyles,
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  ...commonListItemTextTypographyProps(isActive),
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              />
            </ListItemButton>
          )}
        </ScrollNavigationMenuItem>
      ))}
    </List>
  )
}
